import React, { useState, useEffect } from 'react';
import GrantitudeSpiral from './GrantitudeSpiral';

/**
 * Universal Lore Library
 * Planetary family scrapbook/bulletin board of artifacts
 */
const LoreLibraryPage = ({ onNavigateToOnboarding }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);

  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.15) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  // Sample lore artifacts
  const sampleArtifacts = [
    { type: 'photo', title: 'Summer Gathering 2024', family: 'Anonymous', date: '2024-06-15', rotation: -3 },
    { type: 'quote', text: 'Love is the bridge between you and everything', author: 'Rumi', rotation: 2 },
    { type: 'photo', title: 'Three Generations', family: 'Anonymous', date: '2024-08-20', rotation: 5 },
    { type: 'note', text: 'Remember: the family that plays together, stays together', rotation: -2 },
    { type: 'photo', title: 'First Steps Together', family: 'Anonymous', date: '2024-09-10', rotation: 4 },
    { type: 'recipe', title: 'Grandma\'s Secret Bread', family: 'Anonymous', rotation: -4 },
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative">
      
      <div className="fixed inset-0 pointer-events-none opacity-5" style={{ zIndex: 1 }}>
        <div className="absolute inset-0 flex items-center justify-center" style={{ transform: `scale(8) rotate(${spiralPhase}deg)`, filter: 'blur(4px) brightness(1.3) saturate(1.5)', mixBlendMode: 'overlay' }}>
          <GrantitudeSpiral size={300} />
        </div>
      </div>

      <style>{`
        .scrapbook-item {
          background: linear-gradient(135deg, rgba(255, 250, 240, 0.95), rgba(255, 248, 230, 0.9));
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
          transition: all 0.3s ease;
        }
        
        .scrapbook-item:hover {
          transform: scale(1.05) rotate(0deg) !important;
          box-shadow: 0 10px 40px rgba(230, 198, 133, 0.4);
          z-index: 10;
        }
        
        .glass-block {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.03));
          backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.5);
        }
      `}</style>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-16">
        
        <h1 className="text-5xl md:text-6xl font-bold text-center mb-8" style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.15em', color: '#E6C794', textShadow: '0 0 30px rgba(230, 198, 133, 0.4)' }}>
          Lore Library
        </h1>

        <p className="text-xl text-center text-white/80 mb-16" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          The Planetary Family Scrapbook
        </p>

        {/* Scrapbook UI */}
        <div className="relative min-h-[600px] rounded-3xl p-12 mb-16" style={{ background: 'linear-gradient(135deg, rgba(139, 90, 43, 0.3), rgba(101, 67, 33, 0.2))', border: '2px solid rgba(230, 198, 133, 0.3)' }}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {sampleArtifacts.map((artifact, idx) => (
              <div key={idx} className="scrapbook-item rounded-lg p-6 cursor-pointer" style={{ transform: `rotate(${artifact.rotation}deg)`, minHeight: '200px' }}>
                {artifact.type === 'photo' && (
                  <>
                    <div className="w-full h-32 bg-gradient-to-br from-amber-100 to-amber-50 rounded mb-3 flex items-center justify-center">
                      <span className="text-6xl opacity-30">📸</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800">{artifact.title}</p>
                    <p className="text-xs text-gray-600 mt-1">{artifact.date}</p>
                  </>
                )}
                {artifact.type === 'quote' && (
                  <div className="flex flex-col justify-center h-full">
                    <p className="text-base italic text-gray-800 mb-2">"{artifact.text}"</p>
                    <p className="text-xs text-gray-600">— {artifact.author}</p>
                  </div>
                )}
                {artifact.type === 'note' && (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-sm text-gray-800 leading-relaxed">{artifact.text}</p>
                  </div>
                )}
                {artifact.type === 'recipe' && (
                  <div className="flex flex-col justify-center h-full">
                    <span className="text-5xl mb-3">🍞</span>
                    <p className="text-base font-semibold text-gray-800">{artifact.title}</p>
                    <p className="text-xs text-gray-600 mt-1">{artifact.family}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="glass-block rounded-3xl p-10 mb-8 text-center">
          <p className="text-lg leading-relaxed text-white/90 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            A universal scrapbook of family artifacts, photographs, wisdom, and joy. Families give permission to share pieces of their lore, creating a living planetary family archive.
          </p>
          <p className="text-base text-white/70" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Associated with Conscious Stock—our commercial media exchange aggregator.
          </p>
        </div>

        <div className="text-center mt-12">
          <button onClick={onNavigateToOnboarding} className="glass-block px-12 py-5 hover:bg-white/15 border-2 border-amber-500/40 hover:border-amber-500/70 text-white rounded-2xl text-xl transition-all hover:scale-105" style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}>
            Contribute to the Library →
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoreLibraryPage;
